import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  approveKYC,
  rejectKYC,
  requestMoreInfoKYC,
  updateKYCAdmin,
  getAssociateProfile,
  getKYCByAssociateId,
} from '../kycAdminService';

const REVIEW_STEPS = {
  ADDRESS: 1,
  TAX_INFO: 2,
  ID_DOCUMENTS: 3,
  SUMMARY: 4,
};

const STEP_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

const KYCReviewModalSteps = ({ kyc: initialKyc, onClose, onActionComplete }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(REVIEW_STEPS.ADDRESS);
  const [isProcessing, setIsProcessing] = useState(false);
  const [associateProfile, setAssociateProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [kyc, setKyc] = useState(initialKyc);

  // Track status of each step
  const [stepStatuses, setStepStatuses] = useState({
    [REVIEW_STEPS.ADDRESS]: STEP_STATUS.PENDING,
    [REVIEW_STEPS.TAX_INFO]: STEP_STATUS.PENDING,
    [REVIEW_STEPS.ID_DOCUMENTS]: STEP_STATUS.PENDING,
  });

  // Track rejection notes for each step
  const [stepNotes, setStepNotes] = useState({});
  const [currentStepNote, setCurrentStepNote] = useState('');
  const [internalNotes, setInternalNotes] = useState(initialKyc.internalNotes || '');

  // Checklist tracking per step — each key is a step number, value is a Set of checked item indices
  const [checkedItems, setCheckedItems] = useState({
    [REVIEW_STEPS.ADDRESS]: {},
    [REVIEW_STEPS.TAX_INFO]: {},
    [REVIEW_STEPS.ID_DOCUMENTS]: {},
  });

  const toggleCheckItem = (step, idx) => {
    setCheckedItems((prev) => ({
      ...prev,
      [step]: { ...prev[step], [idx]: !prev[step]?.[idx] },
    }));
  };

  const getChecklistItemCount = (step) => {
    const counts = {
      [REVIEW_STEPS.ADDRESS]: 7,
      [REVIEW_STEPS.TAX_INFO]: kyc?.countryCode === 'US' ? 4 : 5,
      [REVIEW_STEPS.ID_DOCUMENTS]: 5,
    };
    return counts[step] || 0;
  };

  const isStepChecklistComplete = (step) => {
    const total = getChecklistItemCount(step);
    const checked = checkedItems[step] || {};
    for (let i = 0; i < total; i++) {
      if (!checked[i]) return false;
    }
    return true;
  };

  const getCheckedCount = (step) => {
    const checked = checkedItems[step] || {};
    return Object.values(checked).filter(Boolean).length;
  };

  // Flag Issue dialog state
  const [flagDialog, setFlagDialog] = useState(null);

  // Confirmation dialog state (replaces native window.confirm)
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showConfirm = ({ title, message, variant = 'primary', confirmLabel = 'Confirm', onConfirm }) => {
    setConfirmDialog({ title, message, variant, confirmLabel, onConfirm });
  };

  const getAddress = (profile) => {
    const b = profile?.billingAddress || {};
    return {
      line1: b.line1 || b.address1 || b.addressLine1 || '',
      line2: b.line2 || b.address2 || b.addressLine2 || '',
      city: b.city || '',
      state: b.state || b.stateCode || b.stateProvince || '',
      postalCode: b.postalCode || b.zip || '',
      country: b.countryCode || b.country || '',
    };
  };

  // Fetch full KYC record and associate profile when modal opens
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch full KYC record (GetAllKYC may not include document fields)
        const fullKyc = await getKYCByAssociateId(initialKyc.associateId);
        if (fullKyc) {
          console.log('[KYCReviewModal] Fetched full KYC record:', fullKyc);
          setKyc({ ...initialKyc, ...fullKyc });
        }
      } catch (err) {
        console.error('[KYCReviewModal] Error fetching full KYC:', err);
      }

      try {
        const profile = await getAssociateProfile(initialKyc.associateId);
        setAssociateProfile(profile);
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    fetchData();
  }, [initialKyc.associateId]);

  const getStepTitle = (step) => {
    const titles = {
      [REVIEW_STEPS.ADDRESS]: 'Step 1: Address & Proof of Address',
      [REVIEW_STEPS.TAX_INFO]: 'Step 2: Tax Information',
      [REVIEW_STEPS.ID_DOCUMENTS]: 'Step 3: Identity Documents',
      [REVIEW_STEPS.SUMMARY]: 'Final Review',
    };
    return titles[step] || '';
  };

  const handleStepApprove = () => {
    if (currentStep === REVIEW_STEPS.SUMMARY) {
      handleFinalApproval();
      return;
    }

    setStepStatuses({
      ...stepStatuses,
      [currentStep]: STEP_STATUS.APPROVED,
    });
    setCurrentStepNote('');
    goToNextStep();
  };

  const handleStepReject = () => {
    setFlagDialog({
      step: currentStep,
      stepTitle: getStepTitle(currentStep),
      note: stepNotes[currentStep] || '',
    });
  };

  const confirmFlag = () => {
    if (!flagDialog || !flagDialog.note.trim()) {
      toast.error('Please describe the issue');
      return;
    }

    setStepStatuses({
      ...stepStatuses,
      [flagDialog.step]: STEP_STATUS.REJECTED,
    });
    setStepNotes({
      ...stepNotes,
      [flagDialog.step]: flagDialog.note,
    });
    setCurrentStepNote('');
    setFlagDialog(null);
    goToNextStep();
  };

  const goToNextStep = () => {
    if (currentStep < REVIEW_STEPS.SUMMARY) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > REVIEW_STEPS.ADDRESS) {
      setCurrentStep(currentStep - 1);
      // Restore note if going back to a rejected step
      if (stepNotes[currentStep - 1]) {
        setCurrentStepNote(stepNotes[currentStep - 1]);
      }
    }
  };

  const executeSendBack = async () => {
    const rejectionDetails = [];
    Object.entries(stepStatuses).forEach(([step, status]) => {
      if (status === STEP_STATUS.REJECTED && stepNotes[step]) {
        const stepName = getStepTitle(parseInt(step));
        rejectionDetails.push(`${stepName}: ${stepNotes[step]}`);
      }
    });

    setIsProcessing(true);
    try {
      const combinedNotes = rejectionDetails.join('\n\n');
      const associateMessage =
        combinedNotes ||
        'Your application has been sent back for corrections. Please review the notes and resubmit.';

      console.log('[KYCReview] executeSendBack — starting', {
        associateId: kyc.associateId,
        currentStatus: kyc.status,
        reviewer: kyc.reviewer,
        reviewerName: kyc.reviewerName,
        kycId: kyc.kycId,
      });

      const result = await requestMoreInfoKYC(
        kyc.associateId,
        internalNotes || kyc.internalNotes || '',
        associateMessage,
        kyc
      );

      console.log('[KYCReview] executeSendBack — completed', result);
      toast.success('Sent back to associate for corrections');
      onActionComplete();
    } catch (err) {
      console.error('[KYCReview] executeSendBack — FAILED:', err);
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send back — check console for details'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendBack = () => {
    showConfirm({
      title: 'Request More Info',
      message:
        'This will send the application back to the associate for corrections. They can update and resubmit. Their reviewer assignment will be kept.',
      variant: 'warning',
      confirmLabel: 'Request More Info',
      onConfirm: executeSendBack,
    });
  };

  const executeDenyApplication = async () => {
    setIsProcessing(true);
    const rejNotes = internalNotes || '';
    const assocMsg =
      'Your KYC application has been denied. Please contact support for assistance.';

    console.log('[KYCReview] executeDenyApplication — starting', {
      associateId: kyc.associateId,
      kycId: kyc.kycId,
    });

    try {
      await rejectKYC(kyc.associateId, rejNotes, assocMsg);
      console.log('[KYCReview] rejectKYC endpoint succeeded');
      toast.success('Application denied');
      onActionComplete();
    } catch (primaryErr) {
      const status = primaryErr?.response?.status;
      console.warn(`[KYCReview] rejectKYC endpoint failed (HTTP ${status}):`, primaryErr);

      if (status === 404) {
        // RejectKYC endpoint doesn't exist — save notes via UpdateKYC but warn about status
        try {
          await updateKYCAdmin(kyc, {
            status: 'REJECTED',
            internalNotes: rejNotes,
            associateNotes: assocMsg,
          });
        } catch (e) {
          console.warn('[KYCReview] UpdateKYC fallback also failed:', e);
        }
        toast.error(
          'Notes saved, but status may not have changed. The RejectKYC endpoint is missing — contact backend team.'
        );
        onActionComplete();
      } else {
        toast.error(primaryErr?.response?.data?.message || 'Failed to deny application');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDenyApplication = () => {
    showConfirm({
      title: 'Reject Application',
      message:
        'This will permanently reject this KYC application. The associate will not be able to resubmit and will need to contact support. This action cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Reject Application',
      onConfirm: executeDenyApplication,
    });
  };

  const executeFinalApproval = async () => {
    setIsProcessing(true);
    try {
      // Ensure submittedDate is set (ApproveKYC may require it)
      if (!kyc.submittedDate) {
        await updateKYCAdmin(kyc, {
          submittedDate: new Date().toISOString(),
          status: 'SUBMITTED',
        });
      }

      await approveKYC(kyc.associateId);
      toast.success(t('kyc.admin.approveSuccess'));
      onActionComplete();
    } catch (err) {
      console.error('Error approving KYC:', err);
      toast.error(err.response?.data?.message || t('kyc.admin.approveFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFinalApproval = () => {
    const hasRejections = Object.values(stepStatuses).some(
      (status) => status === STEP_STATUS.REJECTED
    );

    if (hasRejections) {
      handleSendBack();
      return;
    }

    showConfirm({
      title: 'Approve KYC Application',
      message:
        'This will approve the KYC application and mark the associate as verified. They will be eligible for commission payouts.',
      variant: 'success',
      confirmLabel: 'Approve KYC',
      onConfirm: executeFinalApproval,
    });
  };

  const renderDocument = (url, label) => {
    console.log('[KYCReviewModal] renderDocument:', { label, url });
    if (!url || url === 'string' || url === '') return null;

    return (
      <div className='col-md-6 mb-3'>
        <label className='form-label fw-bold'>{label}</label>
        <div className='border rounded p-2'>
          <a href={url} target='_blank' rel='noopener noreferrer' className='d-block'>
            <img
              src={url}
              alt={label}
              className='img-fluid rounded'
              style={{ maxHeight: '200px', objectFit: 'contain' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{ display: 'none' }} className='text-center py-3'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='48'
                height='48'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                className='text-muted'
              >
                <rect x='3' y='3' width='18' height='18' rx='2' ry='2'></rect>
                <circle cx='8.5' cy='8.5' r='1.5'></circle>
                <polyline points='21 15 16 10 5 21'></polyline>
              </svg>
              <div className='mt-2 text-muted'>Document preview unavailable</div>
              <small>Click to open in new tab</small>
            </div>
          </a>
        </div>
      </div>
    );
  };

  const STEP_LABELS = [
    { key: REVIEW_STEPS.ADDRESS, short: 'Address' },
    { key: REVIEW_STEPS.TAX_INFO, short: 'Tax Info' },
    { key: REVIEW_STEPS.ID_DOCUMENTS, short: 'ID Documents' },
    { key: REVIEW_STEPS.SUMMARY, short: 'Final Review' },
  ];

  const renderProgressBar = () => (
    <div className='mb-4'>
      <div className='d-flex align-items-center gap-1' style={{ overflowX: 'auto' }}>
        {STEP_LABELS.map((step, idx) => {
          const isActive = currentStep === step.key;
          const status = stepStatuses[step.key]; // undefined for SUMMARY
          const isCompleted = status === STEP_STATUS.APPROVED;
          const isRejected = status === STEP_STATUS.REJECTED;
          const isPast = step.key < currentStep;

          let bg = '#e9ecef';
          let color = '#6c757d';
          let border = '1px solid #dee2e6';
          if (isActive) { bg = '#0d6efd'; color = '#fff'; border = '1px solid #0d6efd'; }
          else if (isCompleted) { bg = '#198754'; color = '#fff'; border = '1px solid #198754'; }
          else if (isRejected) { bg = '#dc3545'; color = '#fff'; border = '1px solid #dc3545'; }
          else if (isPast) { bg = '#d1e7fd'; color = '#0d6efd'; border = '1px solid #9ec5fe'; }

          return (
            <React.Fragment key={step.key}>
              {idx > 0 && (
                <div
                  style={{
                    width: '24px',
                    height: '2px',
                    backgroundColor: isPast || isActive ? '#0d6efd' : '#dee2e6',
                    flexShrink: 0,
                  }}
                />
              )}
              <div
                className='d-flex align-items-center gap-1 px-2 py-1 rounded-pill'
                style={{
                  backgroundColor: bg,
                  color,
                  border,
                  fontSize: '0.75rem',
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  cursor: isPast ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (isPast) setCurrentStep(step.key);
                }}
              >
                <span
                  className='d-inline-flex align-items-center justify-content-center rounded-circle'
                  style={{
                    width: '20px',
                    height: '20px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : 'transparent',
                  }}
                >
                  {isCompleted ? '\u2713' : isRejected ? '\u2717' : idx + 1}
                </span>
                {step.short}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case REVIEW_STEPS.ADDRESS:
        return renderAddressVerification();
      case REVIEW_STEPS.TAX_INFO:
        return renderTaxInfo();
      case REVIEW_STEPS.ID_DOCUMENTS:
        return renderIdDocuments();
      case REVIEW_STEPS.SUMMARY:
        return renderSummary();
      default:
        return null;
    }
  };

  const renderAddressVerification = () => {
    const addr = getAddress(associateProfile);
    const countriesMatch = !addr.country || addr.country === kyc.countryCode;
    const hasProofDoc = kyc.proffOfAddressImage && kyc.proffOfAddressImage !== '' && kyc.proffOfAddressImage !== 'string';

    return (
      <div>
        {isLoadingProfile ? (
          <div className='text-center py-5'>
            <div className='spinner-border' role='status'></div>
            <p className='text-muted mt-2'>Loading enrollment data...</p>
          </div>
        ) : (
          <>
            {!countriesMatch && (
              <div className='alert alert-danger mb-3'>
                <strong>Country Mismatch:</strong> Enrollment country ({addr.country}) does
                not match KYC country ({kyc.countryCode}). Verify carefully.
              </div>
            )}

            <div className='row'>
              {/* LEFT — Content */}
              <div className='col-md-8'>
                {/* Comparison table */}
                <div className='card mb-3'>
                  <div className='card-body p-0'>
                    <table className='table table-bordered mb-0' style={{ fontSize: '0.9rem' }}>
                      <thead className='table-light'>
                        <tr>
                          <th style={{ width: '28%' }}>Field</th>
                          <th style={{ width: '36%' }}>Enrollment</th>
                          <th style={{ width: '36%' }}>KYC</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className='fw-bold'>Name</td>
                          <td>{associateProfile?.firstName || ''} {associateProfile?.lastName || 'N/A'}</td>
                          <td className='text-muted small'>(Verify against ID in Step 3)</td>
                        </tr>
                        <tr>
                          <td className='fw-bold'>Address</td>
                          <td>
                            {addr.line1 || 'N/A'}{addr.line2 ? `, ${addr.line2}` : ''}<br />
                            {addr.city || 'N/A'}, {addr.state || 'N/A'} {addr.postalCode || 'N/A'}
                          </td>
                          <td className='text-muted small'>(Check proof doc below)</td>
                        </tr>
                        <tr className={!countriesMatch ? 'table-danger' : ''}>
                          <td className='fw-bold'>Country</td>
                          <td>{addr.country || 'N/A'}</td>
                          <td>
                            {kyc.countryCode || 'N/A'}
                            {!countriesMatch && <span className='badge bg-danger ms-2'>Mismatch</span>}
                          </td>
                        </tr>
                        <tr>
                          <td className='fw-bold'>Tax Form</td>
                          <td className='text-muted'>—</td>
                          <td>
                            <span className='badge bg-blue'>
                              {kyc.type === 'W9' ? 'W-9 (US)' : 'W-8BEN (Non-US)'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Proof of address document */}
                {hasProofDoc ? (
                  <div className='card mb-0'>
                    <div className='card-header py-2 d-flex justify-content-between align-items-center'>
                      <span className='fw-bold' style={{ fontSize: '0.85rem' }}>
                        Proof of Address Document
                      </span>
                      <a href={kyc.proffOfAddressImage} target='_blank' rel='noopener noreferrer'
                        className='btn btn-sm btn-primary py-0 px-2' style={{ fontSize: '0.75rem' }}>
                        Open Full Size
                      </a>
                    </div>
                    <div className='card-body p-2 text-center'>
                      <a href={kyc.proffOfAddressImage} target='_blank' rel='noopener noreferrer' className='d-block'>
                        <img src={kyc.proffOfAddressImage} alt='Proof of Address' className='img-fluid rounded'
                          style={{ maxHeight: '320px', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div style={{ display: 'none', height: '120px' }}
                          className='align-items-center justify-content-center flex-column text-muted'>
                          <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                            <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z'></path>
                            <polyline points='14 2 14 8 20 8'></polyline>
                          </svg>
                          <small className='mt-1'>Click to open in new tab</small>
                        </div>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className='alert alert-danger mb-0'>
                    <strong>Missing:</strong> Proof of address not uploaded. Cannot approve.
                  </div>
                )}
              </div>

              {/* RIGHT — Checklist */}
              <div className='col-md-4 mb-3'>
                <div className='card' style={{ position: 'sticky', top: 0 }}>
                  <div className='card-header py-2 px-3 bg-light d-flex justify-content-between align-items-center'>
                    <strong style={{ fontSize: '0.85rem' }}>Verification Checklist</strong>
                    <span className={`badge ${isStepChecklistComplete(REVIEW_STEPS.ADDRESS) ? 'bg-success' : 'bg-secondary'}`}
                      style={{ fontSize: '0.7rem' }}>
                      {getCheckedCount(REVIEW_STEPS.ADDRESS)}/{getChecklistItemCount(REVIEW_STEPS.ADDRESS)}
                    </span>
                  </div>
                  <div className='card-body p-0'>
                    <div className='list-group list-group-flush' style={{ fontSize: '0.85rem' }}>
                      {[
                        'Country matches enrollment',
                        <>Name matches: <strong>{associateProfile?.firstName} {associateProfile?.lastName}</strong></>,
                        'Address on doc matches enrollment',
                        'Document dated within 3 months',
                        'Utility bill, bank statement, gov letter, or lease',
                        'No editing or forgery',
                        'No duplicate accounts',
                      ].map((label, idx) => (
                        <label key={idx} className='list-group-item d-flex align-items-start gap-2 py-2 mb-0'>
                          <input
                            type='checkbox'
                            className='form-check-input mt-1 flex-shrink-0'
                            checked={!!checkedItems[REVIEW_STEPS.ADDRESS]?.[idx]}
                            onChange={() => toggleCheckItem(REVIEW_STEPS.ADDRESS, idx)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderTaxInfo = () => {
    const isW9 = kyc.type === 'W9';
    const expectedForm = kyc.countryCode === 'US' ? 'W9' : 'W8BEN';
    const formMismatch = kyc.type && kyc.countryCode && kyc.type !== expectedForm;

    return (
      <div>
        {formMismatch && (
          <div className='alert alert-danger mb-3'>
            <strong>Form Mismatch:</strong> Country is {kyc.countryCode} but form type
            is {isW9 ? 'W-9' : 'W-8BEN'}.
          </div>
        )}

        <div className='row'>
          {/* LEFT — Document preview */}
          <div className='col-md-8'>
            {kyc.taxDoc ? (
              <div className='card mb-0'>
                <div className='card-header py-2 d-flex justify-content-between align-items-center'>
                  <span className='fw-bold' style={{ fontSize: '0.85rem' }}>
                    Tax Form ({isW9 ? 'W-9' : 'W-8BEN'})
                  </span>
                  <a href={kyc.taxDoc} target='_blank' rel='noopener noreferrer'
                    className='btn btn-sm btn-primary py-0 px-2' style={{ fontSize: '0.75rem' }}>
                    Open Full Size
                  </a>
                </div>
                <div className='card-body p-2 text-center'>
                  <a href={kyc.taxDoc} target='_blank' rel='noopener noreferrer' className='d-block'>
                    <img src={kyc.taxDoc} alt='Tax Form' className='img-fluid rounded'
                      style={{ maxHeight: '450px', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                    />
                    <div style={{ display: 'none' }} className='text-center py-4'>
                      <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-muted mb-2'>
                        <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z'></path>
                        <polyline points='14 2 14 8 20 8'></polyline>
                      </svg>
                      <p className='text-muted mb-0'>Click to open in new tab</p>
                    </div>
                  </a>
                </div>
              </div>
            ) : (
              <div className='alert alert-danger mb-0'>
                <strong>Missing:</strong> Tax form not uploaded. Cannot approve without
                a signed {isW9 ? 'W-9' : 'W-8BEN'}.
              </div>
            )}
          </div>

          {/* RIGHT — Checklist */}
          <div className='col-md-4 mb-3'>
            <div className='card' style={{ position: 'sticky', top: 0 }}>
              <div className='card-header py-2 px-3 bg-light d-flex justify-content-between align-items-center'>
                <strong style={{ fontSize: '0.85rem' }}>Verification Checklist</strong>
                <span className={`badge ${isStepChecklistComplete(REVIEW_STEPS.TAX_INFO) ? 'bg-success' : 'bg-secondary'}`}
                  style={{ fontSize: '0.7rem' }}>
                  {getCheckedCount(REVIEW_STEPS.TAX_INFO)}/{getChecklistItemCount(REVIEW_STEPS.TAX_INFO)}
                </span>
              </div>
              <div className='card-body p-0'>
                <div className='list-group list-group-flush' style={{ fontSize: '0.85rem' }}>
                  {[
                    'All fields filled out',
                    'Signed and dated',
                    'Name & address match Step 1',
                    <>
                      Correct form: {isW9 ? 'W-9 (US)' : 'W-8BEN (Non-US)'}
                      {formMismatch && <span className='badge bg-danger ms-1' style={{ fontSize: '0.7rem' }}>MISMATCH</span>}
                    </>,
                    ...(!isW9 ? ['Citizenship country indicated'] : []),
                  ].map((label, idx) => (
                    <label key={idx} className='list-group-item d-flex align-items-start gap-2 py-2 mb-0'>
                      <input
                        type='checkbox'
                        className='form-check-input mt-1 flex-shrink-0'
                        checked={!!checkedItems[REVIEW_STEPS.TAX_INFO]?.[idx]}
                        onChange={() => toggleCheckItem(REVIEW_STEPS.TAX_INFO, idx)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className='card-footer bg-white py-2 px-3' style={{ fontSize: '0.82rem' }}>
                <div className='d-flex justify-content-between mb-1'>
                  <span className='text-muted'>Form</span>
                  <span className={`badge ${isW9 ? 'bg-info' : 'bg-secondary'}`} style={{ fontSize: '0.75rem' }}>
                    {isW9 ? 'W-9' : 'W-8BEN'}
                  </span>
                </div>
                <div className='d-flex justify-content-between mb-1'>
                  <span className='text-muted'>Country</span>
                  <span>{kyc.countryCode || 'N/A'}</span>
                </div>
                <div className='d-flex justify-content-between'>
                  <span className='text-muted'>Document</span>
                  {kyc.taxDoc
                    ? <span className='badge bg-success' style={{ fontSize: '0.7rem' }}>Uploaded</span>
                    : <span className='badge bg-danger' style={{ fontSize: '0.7rem' }}>Missing</span>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderIdDocuments = () => {
    const docs = [
      { url: kyc.selfieImage, key: 'selfieImage' },
      { url: kyc.frontIdImage, key: 'frontIdImage' },
      { url: kyc.backIdImage, key: 'backIdImage' },
      { url: kyc.selfieWithIdImage, key: 'selfieWithIdImage' },
    ];
    const uploadedCount = docs.filter((d) => d.url && d.url !== '' && d.url !== 'string').length;

    const renderDocCard = (url, label, hint) => {
      const hasDoc = url && url !== '' && url !== 'string';
      return (
        <div className='col-6 mb-3'>
          <div className={`card h-100 ${hasDoc ? '' : 'border-danger'}`}>
            <div className='card-header py-1 d-flex justify-content-between align-items-center'>
              <span className='fw-bold' style={{ fontSize: '0.85rem' }}>{label}</span>
              {hasDoc ? (
                <a href={url} target='_blank' rel='noopener noreferrer'
                  className='btn btn-sm btn-outline-primary py-0 px-2'
                  style={{ fontSize: '0.7rem' }}>
                  Open
                </a>
              ) : (
                <span className='badge bg-danger' style={{ fontSize: '0.7rem' }}>Missing</span>
              )}
            </div>
            <div className='card-body p-1 text-center' style={{ minHeight: '150px' }}>
              {hasDoc ? (
                <a href={url} target='_blank' rel='noopener noreferrer' className='d-block'>
                  <img src={url} alt={label} className='img-fluid rounded'
                    style={{ maxHeight: '200px', objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <div style={{ display: 'none', height: '150px' }}
                    className='align-items-center justify-content-center flex-column text-muted'>
                    <small>Click to open</small>
                  </div>
                </a>
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100 text-danger'>
                  <span>Not uploaded</span>
                </div>
              )}
            </div>
            <div className='card-footer py-1 bg-light'>
              <small className='text-muted' style={{ fontSize: '0.75rem' }}>{hint}</small>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div>
        <div className='row'>
          {/* LEFT — Documents */}
          <div className='col-md-8'>
            <h6 className='text-muted mb-2' style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Face Verification
            </h6>
            <div className='row'>
              {renderDocCard(kyc.selfieImage, 'Selfie', 'Clear face photo — match ID?')}
              {renderDocCard(kyc.selfieWithIdImage, 'Selfie with ID', 'Same person? ID readable?')}
            </div>

            <h6 className='text-muted mb-2' style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Government-Issued ID
            </h6>
            <div className='row'>
              {renderDocCard(kyc.frontIdImage, 'ID — Front', 'Name, DOB, photo? Not expired?')}
              {renderDocCard(kyc.backIdImage, 'ID — Back', 'Barcode/MRZ visible? No tampering?')}
            </div>
          </div>

          {/* RIGHT — Checklist */}
          <div className='col-md-4 mb-3'>
            <div className='card' style={{ position: 'sticky', top: 0 }}>
              <div className='card-header py-2 px-3 bg-light d-flex justify-content-between align-items-center'>
                <strong style={{ fontSize: '0.85rem' }}>Verification Checklist</strong>
                <span className={`badge ${isStepChecklistComplete(REVIEW_STEPS.ID_DOCUMENTS) ? 'bg-success' : 'bg-secondary'}`}
                  style={{ fontSize: '0.7rem' }}>
                  {getCheckedCount(REVIEW_STEPS.ID_DOCUMENTS)}/{getChecklistItemCount(REVIEW_STEPS.ID_DOCUMENTS)}
                </span>
              </div>
              <div className='card-body p-0'>
                <div className='list-group list-group-flush' style={{ fontSize: '0.85rem' }}>
                  {[
                    'Selfie matches ID photo',
                    'Selfie-with-ID: same person, ID readable',
                    'ID not expired',
                    <>Name matches: <strong>{associateProfile?.firstName} {associateProfile?.lastName}</strong></>,
                    'No editing or tampering',
                  ].map((label, idx) => (
                    <label key={idx} className='list-group-item d-flex align-items-start gap-2 py-2 mb-0'>
                      <input
                        type='checkbox'
                        className='form-check-input mt-1 flex-shrink-0'
                        checked={!!checkedItems[REVIEW_STEPS.ID_DOCUMENTS]?.[idx]}
                        onChange={() => toggleCheckItem(REVIEW_STEPS.ID_DOCUMENTS, idx)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className='card-footer bg-white py-2 px-3' style={{ fontSize: '0.82rem' }}>
                <div className='d-flex justify-content-between align-items-center'>
                  <span className='text-muted'>
                    {associateProfile?.firstName || ''} {associateProfile?.lastName || 'N/A'}
                  </span>
                  <span className={`badge ${uploadedCount === 4 ? 'bg-success' : 'bg-warning'}`}
                    style={{ fontSize: '0.7rem' }}>
                    {uploadedCount}/4 uploaded
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const renderSummary = () => {
    const hasRejections = Object.values(stepStatuses).some(
      (status) => status === STEP_STATUS.REJECTED
    );
    const allApproved = Object.values(stepStatuses).every(
      (status) => status === STEP_STATUS.APPROVED
    );
    const hasPending = Object.values(stepStatuses).some(
      (status) => status === STEP_STATUS.PENDING
    );

    const stepIcons = {
      [STEP_STATUS.APPROVED]: { icon: '\u2713', bg: '#d1e7dd', border: '#198754', color: '#0f5132' },
      [STEP_STATUS.REJECTED]: { icon: '\u2717', bg: '#f8d7da', border: '#dc3545', color: '#842029' },
      [STEP_STATUS.PENDING]: { icon: '?', bg: '#e9ecef', border: '#6c757d', color: '#495057' },
    };

    const stepShortNames = {
      [REVIEW_STEPS.ADDRESS]: 'Address & Proof',
      [REVIEW_STEPS.TAX_INFO]: 'Tax Info',
      [REVIEW_STEPS.ID_DOCUMENTS]: 'ID Documents',
    };

    return (
      <div>
        {/* Applicant summary */}
        <div className='card mb-3'>
          <div className='card-body py-2 px-3'>
            <div className='row align-items-center'>
              <div className='col-auto'>
                <div
                  className='d-flex align-items-center justify-content-center rounded-circle bg-light'
                  style={{ width: '48px', height: '48px', fontSize: '1.2rem', fontWeight: 700 }}
                >
                  {(associateProfile?.firstName || '?')[0]}
                  {(associateProfile?.lastName || '?')[0]}
                </div>
              </div>
              <div className='col'>
                <strong style={{ fontSize: '1.05rem' }}>
                  {associateProfile?.firstName || ''} {associateProfile?.lastName || 'N/A'}
                </strong>
                <div className='text-muted' style={{ fontSize: '0.85rem' }}>
                  ID: {kyc.associateId} &middot; Country: {kyc.countryCode || 'N/A'} &middot;{' '}
                  Form: {kyc.type === 'W9' ? 'W-9' : 'W-8BEN'}
                </div>
              </div>
              <div className='col-auto'>
                {allApproved && <span className='badge bg-success' style={{ fontSize: '0.85rem' }}>Ready to Approve</span>}
                {hasRejections && <span className='badge bg-warning text-dark' style={{ fontSize: '0.85rem' }}>Changes Needed</span>}
                {!allApproved && !hasRejections && <span className='badge bg-secondary' style={{ fontSize: '0.85rem' }}>Incomplete</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Step result cards */}
        <div className='row mb-3'>
          {Object.entries(stepStatuses).map(([step, status]) => {
            const s = stepIcons[status];
            const hasNote = stepNotes[step];
            return (
              <div key={step} className='col-md-4 col-6 mb-2'>
                <div
                  className='card h-100'
                  style={{ borderLeft: `4px solid ${s.border}`, cursor: 'pointer' }}
                  onClick={() => setCurrentStep(parseInt(step))}
                >
                  <div className='card-body py-2 px-3'>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                      <span
                        className='d-inline-flex align-items-center justify-content-center rounded-circle'
                        style={{
                          width: '24px', height: '24px', fontSize: '0.75rem', fontWeight: 700,
                          backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}`,
                        }}
                      >
                        {s.icon}
                      </span>
                      <span className='fw-bold' style={{ fontSize: '0.8rem' }}>
                        {stepShortNames[parseInt(step)]}
                      </span>
                    </div>
                    {hasNote && (
                      <div
                        className='text-danger mt-1'
                        style={{ fontSize: '0.75rem', lineHeight: '1.3' }}
                      >
                        {stepNotes[step]}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Outcome guidance */}
        {allApproved && (
          <div className='alert alert-success mb-3'>
            <strong>All steps passed.</strong> Click <em>Approve KYC</em> below to verify
            this associate. They will become eligible for commission payouts.
          </div>
        )}
        {hasRejections && !hasPending && (
          <div className='alert alert-warning mb-3'>
            <strong>Some steps were flagged.</strong> Choose an action:
            <ul className='mb-0 mt-1'>
              <li>
                <strong>Request More Info</strong> — sends the application back to the
                associate with your notes. They can correct and resubmit.
              </li>
              <li>
                <strong>Reject</strong> — permanent rejection. Use only for fraud or
                compliance violations. The associate cannot resubmit.
              </li>
            </ul>
          </div>
        )}
        {hasPending && (
          <div className='alert alert-secondary mb-3'>
            <strong>Review not complete.</strong> Go back and approve or flag each step
            before you can take a final action. Click any step card above to jump back.
          </div>
        )}

        {/* Internal notes */}
        <div className='card mb-0'>
          <div className='card-body py-2 px-3'>
            <label className='form-label fw-bold mb-1' style={{ fontSize: '0.85rem' }}>
              Internal Notes <span className='text-muted fw-normal'>(admin-only, not visible to associate)</span>
            </label>
            <textarea
              className='form-control'
              rows='2'
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder='Any notes for your records...'
            />
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirmDialog) return null;
    const { title, message, variant, confirmLabel, onConfirm } = confirmDialog;
    return (
      <>
        <div
          className='modal-backdrop fade show'
          style={{ zIndex: 1060 }}
          onClick={() => setConfirmDialog(null)}
        ></div>
        <div
          className='modal fade show d-block'
          tabIndex='-1'
          style={{ zIndex: 1070 }}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{title}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setConfirmDialog(null)}
                ></button>
              </div>
              <div className='modal-body'>
                <p className='mb-0'>{message}</p>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setConfirmDialog(null)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className={`btn btn-${variant}`}
                  onClick={() => {
                    setConfirmDialog(null);
                    onConfirm();
                  }}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderFlagDialog = () => {
    if (!flagDialog) return null;
    return (
      <>
        <div
          className='modal-backdrop fade show'
          style={{ zIndex: 1060 }}
          onClick={() => setFlagDialog(null)}
        ></div>
        <div
          className='modal fade show d-block'
          tabIndex='-1'
          style={{ zIndex: 1070 }}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Flag Issue</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setFlagDialog(null)}
                ></button>
              </div>
              <div className='modal-body'>
                <p className='text-muted mb-2' style={{ fontSize: '0.9rem' }}>
                  <strong>{flagDialog.stepTitle}</strong>
                </p>
                <label className='form-label fw-bold' style={{ fontSize: '0.85rem' }}>
                  What&apos;s the issue?
                </label>
                <textarea
                  className='form-control'
                  rows='3'
                  value={flagDialog.note}
                  onChange={(e) =>
                    setFlagDialog({ ...flagDialog, note: e.target.value })
                  }
                  placeholder='Describe what needs to be corrected...'
                  autoFocus
                />
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setFlagDialog(null)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-warning'
                  onClick={confirmFlag}
                  disabled={!flagDialog.note.trim()}
                >
                  Flag Issue
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {/* Flag Issue Dialog */}
      {renderFlagDialog()}

      {/* Confirmation Dialog */}
      {renderConfirmDialog()}

      {/* Backdrop */}
      <div
        className='modal-backdrop fade show'
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className='modal fade show d-block'
        tabIndex='-1'
        role='dialog'
        style={{ zIndex: 1050 }}
      >
        <div className='modal-dialog modal-xl modal-dialog-scrollable' role='document'>
          <div className='modal-content'>
            {/* Header */}
            <div className='modal-header'>
              <h5 className='modal-title'>
                {t('kyc.admin.reviewTitle')} - {getStepTitle(currentStep)}
              </h5>
              <button
                type='button'
                className='btn-close'
                onClick={onClose}
                disabled={isProcessing}
              ></button>
            </div>

            {/* Body */}
            <div className='modal-body'>
              {renderProgressBar()}
              {renderStepContent()}
            </div>

            {/* Footer */}
            <div className='modal-footer'>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={goToPreviousStep}
                disabled={currentStep === REVIEW_STEPS.ADDRESS || isProcessing}
              >
                ← Previous
              </button>

              {currentStep !== REVIEW_STEPS.SUMMARY ? (
                <>
                  <button
                    type='button'
                    className='btn btn-outline-warning'
                    onClick={handleStepReject}
                    disabled={isProcessing}
                    title='Flag an issue on this step — you can request more info on the summary page'
                  >
                    Flag Issue
                  </button>
                  <button
                    type='button'
                    className='btn btn-success'
                    onClick={handleStepApprove}
                    disabled={isProcessing || !isStepChecklistComplete(currentStep)}
                    title={
                      !isStepChecklistComplete(currentStep)
                        ? `Complete all checklist items (${getCheckedCount(currentStep)}/${getChecklistItemCount(currentStep)})`
                        : ''
                    }
                  >
                    {!isStepChecklistComplete(currentStep)
                      ? `Approve Step (${getCheckedCount(currentStep)}/${getChecklistItemCount(currentStep)})`
                      : 'Approve Step →'
                    }
                  </button>
                </>
              ) : (
                <div className='d-flex gap-2'>
                  {Object.values(stepStatuses).some((s) => s === STEP_STATUS.REJECTED) ? (
                    <>
                      <div className='d-flex flex-column gap-1'>
                        <button
                          type='button'
                          className='btn btn-danger'
                          onClick={handleDenyApplication}
                          disabled={
                            isProcessing ||
                            Object.values(stepStatuses).some(
                              (s) => s === STEP_STATUS.PENDING
                            )
                          }
                          title='Permanent rejection — associate cannot resubmit'
                        >
                          {isProcessing && (
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                            ></span>
                          )}
                          Reject
                        </button>
                        <small
                          className='text-muted text-center'
                          style={{ fontSize: '0.7rem' }}
                        >
                          Permanent — no resubmit
                        </small>
                      </div>
                      <div className='d-flex flex-column gap-1'>
                        <button
                          type='button'
                          className='btn btn-warning'
                          onClick={handleSendBack}
                          disabled={
                            isProcessing ||
                            Object.values(stepStatuses).some(
                              (s) => s === STEP_STATUS.PENDING
                            )
                          }
                          title='Request more info — associate can update and resubmit'
                        >
                          {isProcessing && (
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                            ></span>
                          )}
                          Request More Info
                        </button>
                        <small
                          className='text-muted text-center'
                          style={{ fontSize: '0.7rem' }}
                        >
                          Associate can correct &amp; resubmit
                        </small>
                      </div>
                    </>
                  ) : (
                    <button
                      type='button'
                      className='btn btn-success'
                      onClick={handleFinalApproval}
                      disabled={
                        isProcessing ||
                        Object.values(stepStatuses).some((s) => s === STEP_STATUS.PENDING)
                      }
                    >
                      {isProcessing && (
                        <span
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                        ></span>
                      )}
                      Approve
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

KYCReviewModalSteps.propTypes = {
  kyc: PropTypes.shape({
    kycId: PropTypes.number,
    associateId: PropTypes.number.isRequired,
    status: PropTypes.string,
    countryCode: PropTypes.string,
    type: PropTypes.string,
    submittedDate: PropTypes.string,
    reviewer: PropTypes.string,
    internalNotes: PropTypes.string,
    associateNotes: PropTypes.string,
    selfieImage: PropTypes.string,
    frontIdImage: PropTypes.string,
    backIdImage: PropTypes.string,
    selfieWithIdImage: PropTypes.string,
    taxDoc: PropTypes.string,
    proffOfAddressImage: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onActionComplete: PropTypes.func.isRequired,
};

export default KYCReviewModalSteps;
